import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getImageUrl } from '../utils/cdnUtils'
import { getTenantId } from '../utils/tenantUtils'
import {
  publicDepositAccountService,
  PublicDepositAccount,
  DepositChannels,
  getPublicPromptPayAccounts,
  getDepositChannels,
  getDepositPresetAmounts,
  syncPresetAmountsAfterDepositChannelsUpdate,
  mapPromptPayAccountsToDepositAccounts,
  isTrueMoneyDepositAccount,
  resolveDepositAccountForAmount
} from '../services/depositAccountService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import { useDepositAccountUpdates } from '../hooks/useDepositAccountUpdates'
import { useAuth } from '../contexts/AuthContext'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import { useUserInfo } from '../contexts/UserInfoContext'

export type DepositMethod = 'bank_transfer' | 'prompt_pay' | 'true_money' | 'decimal_reservation'

const DEPOSIT_METHODS: { id: DepositMethod; name: string; description: string; icon: string; recommended?: boolean; banner?: string }[] = [
  {
    id: 'bank_transfer',
    name: 'ธนาคาร',
    description: 'โอนเงินเข้าบัญชีที่ระบบกำหนด แล้วอัปโหลดสลิปแจ้งเติมเครดิต',
    icon: 'fas fa-university'
  },
  {
    id: 'prompt_pay',
    name: 'PROMPTPAY',
    description: 'โอนผ่านพร้อมเพย์ไปยังบัญชีที่ระบบกำหนด แล้วอัปโหลดสลิปแจ้งเติมเครดิต',
    icon: 'fas fa-qrcode'
  },
  {
    id: 'true_money',
    name: 'ทรูมันนี่',
    description: 'โอนผ่าน TrueMoney Wallet ไปยังวอลเลตที่ระบบกำหนด แล้วอัปโหลดสลิปแจ้งเติมเครดิต',
    icon: 'fas fa-mobile-alt'
  },
  {
    id: 'decimal_reservation',
    name: 'ฝากทศนิยม',
    description: 'จองจำนวนทศนิยม (เช่น 213.50) โอนตามจำนวนที่จอง แล้วแจ้งฝาก',
    icon: 'fas fa-calculator'
  }
]

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const DEFAULT_DEPOSIT_CHANNELS: DepositChannels = {
  bank: true,
  trueMoney: true,
  promptPay: true,
  decimalReservation: true,
  allowSubmitWithoutSlip: true,
}

const getMethodDisplayName = (method: DepositMethod, _paymentGatewayEnabled: boolean): string => {
  if (method === 'bank_transfer') return 'ธนาคาร'
  return DEPOSIT_METHODS.find((item) => item.id === method)?.name || ''
}

const getMethodDescription = (method: DepositMethod, paymentGatewayEnabled: boolean): string => {
  if (paymentGatewayEnabled && method === 'bank_transfer') {
    return 'กดยืนยันยอดแล้วระบบจะสร้าง QR หรือข้อมูลสำหรับชำระเงิน จากนั้นแนบสลิปเพื่อส่งรายการฝาก'
  }
  return DEPOSIT_METHODS.find((item) => item.id === method)?.description || ''
}

const buildSenderAccountSnapshot = (account?: {
  bankCode?: string
  bankName?: string
  accountNumber?: string
  accountName?: string
} | null) => {
  if (!account) return undefined
  return {
    bankCode: account.bankCode || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountName: account.accountName || '',
  }
}

const TopupPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, isLoading: isAuthLoading } = useAuth()
  const { themeConfig } = useTenantThemeConfig()
  const { isLoadingUserInfo, getPrimaryBankAccount } = useUserInfo()
  const [selectedMethod, setSelectedMethod] = useState<DepositMethod | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [promptPayAccounts, setPromptPayAccounts] = useState<PublicDepositAccount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [depositChannels, setDepositChannels] = useState<DepositChannels>(DEFAULT_DEPOSIT_CHANNELS)
  const [presetAmounts, setPresetAmounts] = useState<number[]>([100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000])
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)

  const applyDepositChannels = (channels?: Partial<DepositChannels> | null) => {
    if (!channels || typeof channels !== 'object') return false

    setDepositChannels({
      bank: channels.bank !== false,
      trueMoney: channels.trueMoney !== false,
      promptPay: channels.promptPay !== false,
      decimalReservation: channels.decimalReservation !== false,
      allowSubmitWithoutSlip: channels.allowSubmitWithoutSlip !== false,
    })

    return true
  }

  // Get tenant ID using utility function
  const getTenantIdValue = (): string => {
    return getTenantId(user)
  }

  // Load deposit accounts
  const loadDepositAccounts = async () => {
    try {
      setIsLoading(true)
      const tenantId = getTenantIdValue()
      console.log('🏦 Loading deposit accounts for tenant:', tenantId)
      
      const [accounts, promptPayList] = await Promise.all([
        publicDepositAccountService.getActiveDepositAccounts(tenantId),
        getPublicPromptPayAccounts(tenantId),
      ])
      setDepositAccounts(accounts)
      setPromptPayAccounts(mapPromptPayAccountsToDepositAccounts(promptPayList))

      const defaultAcc = accounts.find(acc => acc.isDefault) || accounts[0] || null
      console.log('✅ Loaded deposit accounts:', accounts.length, 'accounts, default:', defaultAcc?.accountName)
    } catch (error) {
      console.error('❌ Error loading deposit accounts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // WebSocket updates for deposit accounts
  useDepositAccountUpdates({
    tenantId: getTenantIdValue(),
    enabled: true,
    onAccountsUpdate: () => {
      console.log('🔄 Deposit accounts updated via WebSocket, reloading deposit targets')
      loadDepositAccounts()
    },
    onDefaultAccountChange: (account) => {
      console.log('⭐ Default account changed:', account.accountName)
      void account
    },
    onAccountStatusChange: (account, newStatus) => {
      console.log(`🔄 Account ${account.accountName} status changed to:`, newStatus)
      // Reload accounts to get updated status
      loadDepositAccounts()
    }
  })

  useEffect(() => {
    if (isAuthLoading) return
    loadDepositAccounts()
  }, [isAuthLoading, user?.tenantId])

  useEffect(() => {
    if (isAuthLoading) return
    const tid = getTenantIdValue()
    if (!tid) return
    Promise.all([
      getDepositChannels(tid),
      getDepositPresetAmounts(tid),
      getDepositFlowConfig(tid),
    ]).then(([channels, amounts, flow]) => {
      setDepositChannels(channels)
      setPresetAmounts(amounts)
      setDepositFlow(flow)
    })
  }, [isAuthLoading, user?.tenantId])

  useEffect(() => {
    if (isAuthLoading) return
    const tid = getTenantIdValue()
    if (!tid) return

    const handleDepositChannelsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{
        tenantId?: string
        content?: {
          depositChannels?: Partial<DepositChannels>
          settings?: { depositChannels?: Partial<DepositChannels> }
        }
      }>
      if (customEvent.detail?.tenantId && customEvent.detail.tenantId !== tid) return

      const contentChannels =
        customEvent.detail?.content?.depositChannels ||
        customEvent.detail?.content?.settings?.depositChannels

      if (applyDepositChannels(contentChannels)) {
        void syncPresetAmountsAfterDepositChannelsUpdate(tid, contentChannels).then(setPresetAmounts)
        getDepositFlowConfig(tid).then(setDepositFlow)
        return
      }

      getDepositChannels(tid, { forceRefresh: true }).then((ch) => setDepositChannels(ch))
      void syncPresetAmountsAfterDepositChannelsUpdate(tid, contentChannels).then(setPresetAmounts)
      getDepositFlowConfig(tid).then(setDepositFlow)
    }

    window.addEventListener('deposit_channels_updated', handleDepositChannelsUpdated)
    return () => window.removeEventListener('deposit_channels_updated', handleDepositChannelsUpdated)
  }, [isAuthLoading, user?.tenantId])

  useEffect(() => {
    if (isAuthLoading) return
    const tid = getTenantIdValue()
    if (!tid) return

    const handleDepositAccountsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ tenantId?: string }>
      if (customEvent.detail?.tenantId && customEvent.detail.tenantId !== tid) return
      loadDepositAccounts()
    }

    window.addEventListener('deposit_accounts_updated', handleDepositAccountsUpdated)
    return () => window.removeEventListener('deposit_accounts_updated', handleDepositAccountsUpdated)
  }, [isAuthLoading, user?.tenantId])

  const enteredAmount = useMemo(() => {
    const normalizedValue = customAmount.replace(/,/g, '')
    const amount = parseFloat(normalizedValue)
    return Number.isFinite(amount) ? amount : NaN
  }, [customAmount])
  const trueMoneyAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredAmount, { channel: 'true_money' }),
    [depositAccounts, enteredAmount]
  )
  const promptPayAccount = useMemo(
    () => resolveDepositAccountForAmount(promptPayAccounts, enteredAmount, { channel: 'promptpay' }),
    [enteredAmount, promptPayAccounts]
  )
  const hasBankAmountRanges = useMemo(
    () =>
      depositAccounts.some(
        (account) =>
          typeof account.minDepositAmount === 'number' &&
          typeof account.maxDepositAmount === 'number' &&
          !isTrueMoneyDepositAccount(account)
      ),
    [depositAccounts]
  )
  const hasTrueMoneyAmountRanges = useMemo(
    () =>
      depositAccounts.some(
        (account) =>
          typeof account.minDepositAmount === 'number' &&
          typeof account.maxDepositAmount === 'number' &&
          isTrueMoneyDepositAccount(account)
      ),
    [depositAccounts]
  )
  const hasPromptPayAmountRanges = useMemo(
    () =>
      promptPayAccounts.some(
        (account) =>
          typeof account.minDepositAmount === 'number' &&
          typeof account.maxDepositAmount === 'number'
      ),
    [promptPayAccounts]
  )
  const hasManualBankAccounts = useMemo(
    () => depositAccounts.some((account) => !isTrueMoneyDepositAccount(account) && account.bankCode !== 'PROMPTPAY'),
    [depositAccounts]
  )
  const selectedDepositAccount = useMemo(() => {
    if (selectedMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled) {
      return null
    }

    if (selectedMethod === 'prompt_pay') {
      return promptPayAccount
    }

    if (selectedMethod === 'true_money') {
      return trueMoneyAccount
    }

    return resolveDepositAccountForAmount(depositAccounts, enteredAmount, { channel: 'bank' })
  }, [depositAccounts, depositFlow.paymentGatewayEnabled, enteredAmount, promptPayAccount, selectedMethod, trueMoneyAccount])
  const userBankAccount = getPrimaryBankAccount()
  const userBankAccountSnapshot = useMemo(() => buildSenderAccountSnapshot(userBankAccount), [userBankAccount])
  const selectedReceiverAccount = useMemo(() => {
    if (selectedMethod === 'decimal_reservation') {
      return resolveDepositAccountForAmount(depositAccounts, enteredAmount, { channel: 'bank' })
    }
    return selectedDepositAccount
  }, [depositAccounts, enteredAmount, selectedDepositAccount, selectedMethod])
  const systemDepositEnabled = themeConfig?.settings?.depositEnabled !== false
  const bankTransferUsesPaymentGateway = selectedMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled
  const shouldShowRangeMissingMessage =
    !bankTransferUsesPaymentGateway &&
    Number.isFinite(enteredAmount) &&
    enteredAmount > 0 &&
    !selectedDepositAccount &&
    (
      (selectedMethod === 'bank_transfer' && hasBankAmountRanges) ||
      (selectedMethod === 'true_money' && hasTrueMoneyAmountRanges) ||
      (selectedMethod === 'prompt_pay' && hasPromptPayAmountRanges)
    )

  const availableMethods = useMemo(
    () =>
      DEPOSIT_METHODS.filter((method) => {
        if (!systemDepositEnabled) {
          return false
        }

        switch (method.id) {
          case 'bank_transfer':
            return depositChannels.bank && (depositFlow.paymentGatewayEnabled || hasManualBankAccounts)
          case 'prompt_pay':
            return depositChannels.promptPay && promptPayAccounts.length > 0
          case 'true_money':
            return depositChannels.trueMoney && depositAccounts.some((account) => isTrueMoneyDepositAccount(account))
          case 'decimal_reservation':
            return depositChannels.decimalReservation && hasManualBankAccounts
          default:
            return true
        }
      }),
    [depositAccounts, depositChannels, depositFlow.paymentGatewayEnabled, hasManualBankAccounts, promptPayAccounts.length, systemDepositEnabled]
  )

  useEffect(() => {
    if (!selectedMethod) return

    const stillEnabled = availableMethods.some((method) => method.id === selectedMethod)
    if (!stillEnabled) {
      setSelectedMethod(null)
    }
  }, [availableMethods, selectedMethod])

  useEffect(() => {
    if (isLoadingUserInfo) return
    if (userBankAccount) return

    navigate('/profile?openAddBank=1', { replace: true })
  }, [isLoadingUserInfo, userBankAccount, navigate])

  if (!systemDepositEnabled) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 px-1">
              <div className="bdr-5 bx-sd mb-2">
                <div className="row mx--1">
                  <div className="col-12 px-1">
                    <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                      <a
                        href="#"
                        className="txt-link-d"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate('/dashboard')
                        }}
                      >
                        <i className="fas fa-chevron-left me-1"></i>
                        หน้าหลัก
                      </a>
                    </div>
                  </div>
                  <div className="col-12 px-1">
                    <div className="w-100 p-4 bgc-7">
                      <div className="text-center">
                        <div className="mb-3" style={{ fontSize: '2rem', color: '#dc3545' }}>
                          <i className="fas fa-ban"></i>
                        </div>
                        <h4 className="mb-2">ระบบฝากเงินปิดชั่วคราว</h4>
                        <p className="mb-0 text-muted">ขณะนี้ผู้ดูแลระบบปิดการฝากเงินทั้งระบบ กรุณาลองใหม่ภายหลัง</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleAmountSelect = (amount: string | number) => {
    // Remove commas and set to input field as whole number
    const cleanAmount = String(amount).replace(/,/g, '')
    setCustomAmount(cleanAmount)
  }

  const handleConfirmAmount = () => {
    if (!customAmount || parseFloat(customAmount) <= 0 || !selectedMethod) return
    if (selectedMethod === 'decimal_reservation') {
      const amountStr = customAmount.replace(/,/g, '')
      if (!/^\d+$/.test(amountStr)) return
      navigate('/topup-reserve', {
        state: {
          amount: amountStr,
          depositAccount: selectedReceiverAccount,
          senderAccount: userBankAccountSnapshot,
        },
      })
      return
    }
    if (!bankTransferUsesPaymentGateway && !selectedDepositAccount) return
    const wholeAmount = Math.floor(parseFloat(customAmount.replace(/,/g, ''))).toString()
    const fallbackBankAccount =
      selectedMethod === 'bank_transfer'
        ? resolveDepositAccountForAmount(depositAccounts, enteredAmount, { channel: 'bank' }) || undefined
        : undefined
    const nextDepositAccount = selectedMethod === 'bank_transfer'
      ? fallbackBankAccount
      : selectedDepositAccount || undefined
    navigate('/topup-confirm', {
      state: {
        amount: wholeAmount,
        depositAccount: nextDepositAccount,
        depositMethod: selectedMethod,
        senderAccount: userBankAccountSnapshot,
        usesPaymentGateway: bankTransferUsesPaymentGateway,
      }
    })
  }

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (selectedMethod === 'decimal_reservation') {
      const cleaned = value.replace(/[^\d]/g, '')
      setCustomAmount(cleaned)
      return
    }
    const wholeNumber = value.replace(/[^\d]/g, '')
    setCustomAmount(wholeNumber)
  }

  // ——— ขั้นตอนที่ 1: เลือกวิธีการฝาก ———
  if (selectedMethod === null) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 px-1">
              <div className="bdr-5 bx-sd mb-2">
                <div className="row mx--1">
                  <div className="col-12 px-1">
                    <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                      <a
                        href="#"
                        className="txt-link-d"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate('/dashboard')
                        }}
                      >
                        <i className="fas fa-chevron-left me-1"></i>
                        หน้าหลัก
                      </a>
                    </div>
                  </div>
                  <div className="col-12 px-1">
                    <div className="w-100 p-2 bgc-7">
                      <div className="text-center mb-4">
                        <h4 className="c-dark t-medium mb-1" style={{ fontSize: '1.35rem' }}>
                          <i className="fas fa-cash-register me-2 c-red"></i>
                          แคชเชียร์
                        </h4>
                        <p className="c-dark mb-0 opacity-85" style={{ fontSize: '0.95rem' }}>
                          เลือกวิธีการฝากเงิน
                        </p>
                      </div>

                      <div className="row g-3 mx--1 align-items-stretch">
                    {availableMethods.length === 0 ? (
                      <div className="col-12 px-1">
                        <div className="bdr-5 p-4 text-center bg-white border">
                          <div className="mb-2" style={{ fontSize: '1.75rem', color: '#6c757d' }}>
                            <i className="fas fa-wallet"></i>
                          </div>
                          <h5 className="mb-2">ยังไม่มีช่องทางฝากเงินที่เปิดใช้งาน</h5>
                          <p className="mb-0 text-muted">กรุณาติดต่อผู้ดูแลระบบ หรือลองใหม่ภายหลัง</p>
                        </div>
                      </div>
                    ) : availableMethods.map((method) => (
                      <div key={method.id} className="col-12 col-md-6 px-1 d-flex">
                        <button
                          type="button"
                          className="w-100 border-0 p-0 bg-transparent text-start h-100 d-block"
                          onClick={() => setSelectedMethod(method.id)}
                        >
                          <div
                            className={method.recommended ? 'bdr-5 h-100' : ''}
                            style={method.recommended ? { padding: 2 } : {}}
                          >
                            <div
                              className="bdr-5 position-relative overflow-hidden h-100 d-flex flex-column"
                              style={{
                                transition: 'all 0.25s ease',
                                background: 'linear-gradient(145deg, #fff 0%, #f8f8f8 100%)',
                                border: method.recommended ? 'none' : '1px solid #dee2e6',
                                boxShadow: method.recommended
                                  ? '0 2px 6px rgba(0,0,0,0.06)'
                                  : '0 2px 10px rgba(0,0,0,0.06)',
                                paddingTop: method.banner ? undefined : '1.25rem',
                                paddingBottom: '1.25rem',
                                paddingLeft: '1.25rem',
                                paddingRight: '1.25rem'
                              }}
                              onMouseOver={(e) => {
                                if (!method.recommended) {
                                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'
                                }
                                e.currentTarget.style.transform = 'translateY(-2px)'
                              }}
                              onMouseOut={(e) => {
                                if (!method.recommended) {
                                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'
                                }
                                e.currentTarget.style.transform = 'translateY(0)'
                              }}
                            >
                            {method.banner && (
                              <div
                                className="text-center py-2 c-white t-regular mb-2 flex-shrink-0"
                                style={{
                                  background: 'linear-gradient(90deg, #b91c1c 0%, #c00 50%, #dc2626 100%)',
                                  fontSize: '0.8rem'
                                }}
                              >
                                {method.banner}
                              </div>
                            )}
                            <div className="d-flex align-items-start gap-3 px-2 pb-2 flex-grow-1 min-h-0">
                              <div
                                className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: 56,
                                  height: 56,
                                  background: method.recommended
                                    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                                    : 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
                                  boxShadow: '0 3px 10px rgba(0,0,0,0.15)'
                                }}
                              >
                                <i className={`${method.icon} c-white`} style={{ fontSize: '1.4rem' }}></i>
                              </div>
                              <div className="min-w-0 flex-grow-1 d-flex flex-column">
                                <div className="d-flex align-items-center gap-2 mb-1 flex-shrink-0">
                                  <h6 className="c-dark t-medium mb-0" style={{ fontSize: '1.05rem' }}>
                                    {getMethodDisplayName(method.id, depositFlow.paymentGatewayEnabled)}
                                  </h6>
                                  {method.recommended && (
                                    <span
                                      className="badge rounded-pill"
                                      style={{ background: '#16a34a', fontSize: '0.65rem' }}
                                    >
                                      แนะนำ
                                    </span>
                                  )}
                                </div>
                                <p
                                  className="c-dark mb-0 opacity-90 lh-sm flex-grow-1"
                                  style={{ fontSize: '0.8rem', lineHeight: 1.45 }}
                                >
                                  {getMethodDescription(method.id, depositFlow.paymentGatewayEnabled)}
                                </p>
                              </div>
                            </div>
                          </div>
                          </div>
                        </button>
                      </div>
                    ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ——— ขั้นตอนที่ 2: ใส่จำนวนเงิน ———
  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        setSelectedMethod(null)
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      เลือกวิธีอื่น
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-wallet me-2"></i>
                      {selectedMethod === 'decimal_reservation'
                        ? 'ฝากทศนิยม'
                        : selectedMethod === 'prompt_pay'
                          ? 'แจ้งเติมเครดิต (PROMPTPAY)'
                          : selectedMethod === 'true_money'
                            ? 'แจ้งเติมเครดิต (ทรูมันนี่)'
                            : 'แจ้งเติมเครดิต'}
                    </div>

                    {selectedMethod === 'decimal_reservation' ? (
                      <div className="bdr-5 bgc-7 border p-2 mb-2">
                        <p className="c-dark fs-14 mb-0">
                          <i className="fas fa-calculator c-red me-2"></i>
                          ใส่จำนวนเงินเต็ม (เช่น 213) แล้วระบบจะสุ่มทศนิยม 2 ตำแหน่งให้อัตโนมัติ
                        </p>
                      </div>
                    ) : selectedMethod === 'prompt_pay' ? (
                      <div className="bdr-5 bgc-7 border p-2 mb-2">
                        <p className="c-dark fs-14 mb-0">
                          <i className="fas fa-qrcode c-red me-2"></i>
                          โอนผ่าน PROMPTPAY ไปยังข้อมูลรับฝากด้านล่าง แล้วแนบสลิปเพื่อแจ้งเติมเครดิต
                        </p>
                      </div>
                    ) : selectedMethod === 'true_money' ? (
                      <div className="bdr-5 bgc-7 border p-2 mb-2">
                        <p className="c-dark fs-14 mb-0">
                          <i className="fas fa-mobile-alt c-red me-2"></i>
                          โอนผ่านทรูมันนี่ไปยังวอลเลตที่ระบบกำหนด แล้วแนบสลิปเพื่อแจ้งเติมเครดิต
                        </p>
                      </div>
                    ) : (
                    <>
                    <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                      <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          <div className="c-dark t-medium text-center">
                            กรุณาโอนเงินจากบัญชีนี้เท่านั้น
                          </div>
                        </div>

                        <div className="col-12 px-1">
                          {isLoading ? (
                            <div className="wrap align-items-center justify-content-center bgc-7 bdr-5 p-3">
                              <div className="c-dark t-medium">
                                <i className="fas fa-spinner fa-spin me-2"></i>
                                กำลังโหลดข้อมูลบัญชี...
                              </div>
                            </div>
                          ) : isLoadingUserInfo ? (
                            <div className="wrap align-items-center justify-content-center bgc-7 bdr-5 p-3">
                              <div className="c-dark t-medium">
                                <i className="fas fa-spinner fa-spin me-2"></i>
                                กำลังโหลดข้อมูลบัญชีผู้ใช้...
                              </div>
                            </div>
                          ) : userBankAccount ? (
                            <div className="wrap align-items-start bgc-7 bdr-5 p-2">
                              <div className="img-b bdr-5 bgc-4">
                                {userBankAccount.imageUrl ? (
                                  <img 
                                    src={userBankAccount.imageUrl} 
                                    alt={userBankAccount.bankName}
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement
                                      target.src = getImageUrl('icons-default-bank.png')
                                    }}
                                  />
                                ) : (
                                  <img src={getImageUrl('icons-default-bank.png')} alt="Default Bank" />
                                )}
                              </div>

                              <div className="txt-b ms-3">
                                <h6 className="c-dark">{userBankAccount.bankName}</h6>
                                <h5 className="c-red t-medium">{userBankAccount.accountNumber}</h5>
                                <h6
                                  className="c-white bgg-5 bdr-5 px-2 mb-1"
                                  style={{ width: 'fit-content' }}
                                >
                                  บัญชีของคุณ
                                </h6>
                                <h5 className="c-dark t-medium fs-16">
                                  {userBankAccount.accountName}
                                </h5>
                              </div>
                            </div>
                          ) : (
                            <div className="wrap align-items-center justify-content-center bgc-7 bdr-5 p-3">
                              <div className="text-center">
                                <i className="fas fa-exclamation-triangle c-warning fs-24 mb-2"></i>
                                <div className="c-dark t-medium">
                                  ยังไม่ได้ผูกบัญชีธนาคาร
                                </div>
                                <div className="c-dark fs-12 mt-1">
                                  กรุณาไปเพิ่มบัญชีธนาคารที่หน้าโปรไฟล์ก่อนทำรายการฝากเงิน
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                      <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          <div className="c-dark t-medium text-center">
                            บัญชีผู้รับที่ระบบเลือกให้
                          </div>
                        </div>

                        <div className="col-12 px-1">
                          {selectedReceiverAccount ? (
                            <div className="wrap align-items-start bgc-7 bdr-5 p-2">
                              <div className="img-b bdr-5 bgc-4">
                                {selectedReceiverAccount.bankIcon ? (
                                  <img
                                    src={selectedReceiverAccount.bankIcon}
                                    alt={selectedReceiverAccount.bankName || 'Deposit account'}
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement
                                      target.src = getImageUrl('icons-default-bank.png')
                                    }}
                                  />
                                ) : (
                                  <img src={getImageUrl('icons-default-bank.png')} alt={selectedReceiverAccount.bankName || 'Deposit account'} />
                                )}
                              </div>

                              <div className="txt-b ms-3">
                                <h6 className="c-dark">{selectedReceiverAccount.bankName || '-'}</h6>
                                <h5 className="c-red t-medium">{selectedReceiverAccount.accountNumber || '-'}</h5>
                                <h6
                                  className="c-white bgg-5 bdr-5 px-2 mb-1"
                                  style={{ width: 'fit-content' }}
                                >
                                  บัญชีผู้รับ
                                </h6>
                                <h5 className="c-dark t-medium fs-16">
                                  {selectedReceiverAccount.accountName || '-'}
                                </h5>
                              </div>
                            </div>
                          ) : bankTransferUsesPaymentGateway ? (
                            <div className="wrap align-items-center justify-content-center bgc-7 bdr-5 p-3">
                              <div className="text-center">
                                <div className="c-dark t-medium">ช่องทางธนาคารจะข้ามการเก็บส่วนนี้ไว้ก่อน</div>
                              </div>
                            </div>
                          ) : (
                            <div className="wrap align-items-center justify-content-center bgc-7 bdr-5 p-3">
                              <div className="text-center">
                                <div className="c-dark t-medium">กรุณาใส่จำนวนเงินเพื่อให้ระบบเลือกบัญชีรับโอน</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </>
                    )}

                    <div className="bdr-5 bgc-4 c-red p-2">
                      <div className="row mx--1">
                        <div className="col-12 px-1">
                          <div className="c-dark t-medium text-center mb-2">
                            <h6>
                              {selectedMethod === 'decimal_reservation'
                                ? 'ใส่จำนวนเงินเต็ม (บาท) เช่น 213'
                                : 'ใส่จำนวนเงินที่ต้องการเติม/ฝาก'}
                            </h6>
                          </div>

                          <div className="row mx--1">
                            {presetAmounts.length > 0 && presetAmounts.map((amount, index) => (
                              <div key={index} className="col-4 px-1 mb-2">
                                <a
                                  href="#"
                                  className="button-main w-100 bgc-3 c-white t-medium bdr-5 py-2 txt-link-g"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    handleAmountSelect(amount)
                                  }}
                                >
                                  {amount.toLocaleString()}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <div className="bgc-7 bdr-5 px-2 py-2">
                            <p className="c-dark t-medium text-center mb-2">
                              ระบุจำนวนเงิน
                            </p>
                            <input
                              type="text"
                              id="amount"
                              className="input-show text-center bdr-5 border-0"
                              placeholder="ใส่จำนวนเงิน"
                              value={customAmount}
                              onChange={handleCustomAmountChange}
                              inputMode="numeric"
                              pattern="[0-9]*"
                            />
                          </div>
                        </div>

                        <div className="col-12 px-1">
                          {shouldShowRangeMissingMessage && (
                            <div className="text-center mb-2">
                              <span className="c-red fs-12">ยอดนี้ยังไม่มีบัญชีฝากที่ตรงกับ range ที่ตั้งไว้</span>
                            </div>
                          )}
                          <a
                            href="#"
                            className="check-button w-100 bgg-1 c-white"
                            onClick={(e) => {
                              e.preventDefault()
                              handleConfirmAmount()
                            }}
                            style={!customAmount || parseFloat(customAmount) <= 0 || !selectedMethod || (!bankTransferUsesPaymentGateway && !selectedDepositAccount) ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                          >
                            ตกลง
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TopupPage
